/** Pure helpers for walking chapters/steps and resolving transitions. No React, no Firestore. */

export const COMPLETE_TUTORIAL = "@complete";
export const COMPLETE_CHAPTER = "@chapter";

/** Route names a chapter runs on: `routes` when the page can be reached under several names, otherwise `route`. */
export const isChapterRoute = (chapter, routeName) => (chapter?.routes || [chapter?.route]).includes(routeName);

/** Where to send the user to resume a chapter: its `route`, plus any params it needs (e.g. the owner's page uid). */
export const getChapterNavigation = (chapter, userData) => ({ name: chapter.route, params: chapter.getRouteParams?.({ userData }) });

export const getStep = (chapter, stepId) => chapter?.steps.find((step) => step.id === stepId) || null;

export const getStepIndex = (chapter, stepId) => chapter.steps.findIndex((step) => step.id === stepId);

export const getFirstStepId = (chapter) => chapter.steps[0].id;

export const getNextStepId = (chapter, stepId) => chapter.steps[getStepIndex(chapter, stepId) + 1]?.id || null;

/** Back skips silent (transient) steps so the user never lands on an instruction pill for a vanished modal. */
export const getPreviousStepId = (chapter, stepId) => {
	for (let index = getStepIndex(chapter, stepId) - 1; index >= 0; index -= 1) {
		if (!chapter.steps[index].silent) return chapter.steps[index].id;
	}
	return null;
};

export const getVisibleSteps = (chapter) => chapter.steps.filter((step) => !step.silent);

/** "n of N" for the chapter. A silent step reports the nearest preceding visible step. */
export const getChapterProgress = (chapter, stepId) => {
	const visibleSteps = getVisibleSteps(chapter);
	const stepIndex = getStepIndex(chapter, stepId);
	const visibleBefore = chapter.steps.slice(0, stepIndex + 1).filter((step) => !step.silent).length;
	return { current: Math.max(visibleBefore, 1), total: visibleSteps.length };
};

const toArray = (value) => (value ? (Array.isArray(value) ? value : [value]) : []);

const matchesTrigger = (transition, trigger) => {
	if (transition.event) {
		return trigger.type === "event" && trigger.name === transition.event && (!transition.when || transition.when(trigger.payload || {}));
	}
	if (transition.route) {
		return trigger.type === "route" && trigger.name === transition.route;
	}
	if (transition.signal) {
		return trigger.type === "signal" && trigger.scope === transition.signal.scope && (!transition.when || transition.when(trigger.snapshot || {}));
	}
	return false;
};

/** Returns the `goto` of the first transition on `step` matched by `trigger`, or null. */
export const resolveTransition = (step, trigger) => {
	const match = toArray(step?.waitFor).find((transition) => matchesTrigger(transition, trigger));
	return match ? match.goto || "next" : null;
};

/** True when the step's `requires` gate is satisfied (steps without one are always satisfied). */
export const isRequirementMet = (step, getSignals) => {
	if (!step?.requires) return true;
	const snapshot = getSignals(step.requires.scope);
	return Boolean(snapshot) && step.requires.when(snapshot);
};
